/**
 * The three artifacts the public page hands a visitor — `m05-linkedin.js` §PNG / §OG / §print.
 *
 * The reference rasterises through `Engine.planToCanvas`, which this rebuild has not ported as a
 * shared function. The route taken here is the other one the same engine already supports: the
 * plan is drawn to SVG (which IS ported, and is what the page itself shows), then rasterised in an
 * `<img>`. That path cannot reach the document's fonts, so the two Poppins faces are inlined into
 * the SVG as data URIs first — without that a downloaded PNG comes back in the browser's fallback
 * font, which the reference explicitly refuses to call a canonical export.
 *
 * API GAP: a real backend serves `/c/:code/image.png`, `/c/:code/print` and `/c/:code/og.png` from
 * the stored RenderManifest, so the bytes a visitor downloads are the ones the manifest hash was
 * computed over. Everything below is the prototype's client-side stand-in for those three routes.
 */
import type { DemoCredential } from '../data/types';
import { publicAssetUrl } from './asset-url';
import { credentialViewState, credTitle, displayName, fmtDate, publicUrl, trUpper } from './reference';
import { canvasH, canvasW, credTerms } from './render';

const POPPINS = [
  { weight: 400, url: publicAssetUrl('/v07/fonts/Poppins-Regular.ttf') },
  { weight: 700, url: publicAssetUrl('/v07/fonts/Poppins-Bold.ttf') },
];

let fontCssPromise: Promise<string> | null = null;

async function dataUrl(url: string): Promise<string> {
  /* This build's OWN bundled typeface, read as a static file — never an API call. Nothing is
     attached to it, for the same reason the share card attaches nothing: a read that needs no
     identity should not be able to carry one. */
  const res = await fetch(url, { credentials: 'omit', cache: 'force-cache' });
  const blob = await res.blob();
  return await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(new Error(`font okunamadı: ${url}`));
    reader.readAsDataURL(blob);
  });
}

/** Both faces as one `<style>` body, fetched once per session and reused by every export. */
function embeddedFontCss(): Promise<string> {
  fontCssPromise ??= Promise.all(
    POPPINS.map(async (f) => {
      const src = await dataUrl(f.url);
      return `@font-face{font-family:"Poppins";font-style:normal;font-weight:${f.weight};src:url("${src}") format("truetype");}`;
    }),
  )
    .then((parts) => parts.join(''))
    .catch(() => '');
  return fontCssPromise;
}

const loadImage = (src: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('görsel yüklenemedi'));
    img.src = src;
  });

const attrNumber = (svg: string, name: string): number => {
  const m = new RegExp(`${name}="(\\d+(?:\\.\\d+)?)"`).exec(svg);
  return m ? Number(m[1]) : 0;
};

/**
 * The credential's own SVG, rasterised at `scale`.
 *
 * Pass the NON-responsive SVG: `width:100%` on the root leaves an `<img>` with no intrinsic size
 * in some engines, and a zero-width canvas is not a failure anyone would notice until the download
 * arrives empty.
 */
export async function credentialPngDataUrl(svg: string, scale = 1): Promise<string> {
  const css = await embeddedFontCss();
  const withFonts = css ? svg.replace('>', `><style>${css}</style>`) : svg;
  const width = attrNumber(svg, 'width');
  const height = attrNumber(svg, 'height');
  if (!width || !height) throw new Error('Görsel ölçüsü okunamadı');
  const url = URL.createObjectURL(new Blob([withFonts], { type: 'image/svg+xml;charset=utf-8' }));
  try {
    const img = await loadImage(url);
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(width * scale);
    canvas.height = Math.round(height * scale);
    const g = canvas.getContext('2d');
    if (!g) throw new Error('Canvas bağlamı açılamadı');
    g.drawImage(img, 0, 0, canvas.width, canvas.height);
    return canvas.toDataURL('image/png');
  } finally {
    URL.revokeObjectURL(url);
  }
}

/** `downloadDataUrl` — a click on a synthesised anchor, as upstream. */
export function downloadDataUrl(data: string, filename: string): boolean {
  try {
    const a = document.createElement('a');
    a.href = data;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    return true;
  } catch {
    return false;
  }
}

export function openNewTab(url: string): Window | null {
  try {
    const w = window.open(url, '_blank');
    if (w) w.opener = null;
    return w;
  } catch {
    return null;
  }
}

/**
 * `printCredential` — the page size follows the canvas, not the paper the visitor happens to have.
 *
 * A square badge prints on a 200mm square, a landscape certificate on A4 landscape and everything
 * else on A4 portrait, so the artwork is never rescaled to fit a shape it was not designed for.
 */
export async function printCredential(c: DemoCredential, svg: string): Promise<boolean> {
  const w = canvasW(c.release.canvas);
  const h = canvasH(c.release.canvas);
  const square = Math.abs(w - h) < 4;
  const pageSize = square ? '200mm 200mm' : w >= h ? '297mm 210mm' : '210mm 297mm';
  const png = await credentialPngDataUrl(svg, 2);
  const title = `${credTitle(c)} – ${displayName(c)}`;
  const tab = openNewTab('about:blank');
  if (!tab) return false;
  const doc = `<!doctype html><html lang="tr"><head><meta charset="utf-8"><title>${title}</title><style>@page{size:${pageSize};margin:0}html,body{margin:0;height:100%}body{display:flex;align-items:center;justify-content:center;background:#fff}img{max-width:100%;max-height:100vh;display:block}@media screen{body{background:#F5F2EF;padding:24px;box-sizing:border-box}img{box-shadow:0 8px 30px rgba(0,0,0,.15)}}</style></head><body><img src="${png}" alt=""></body></html>`;
  tab.document.open();
  tab.document.write(doc);
  tab.document.close();
  /* The reference prints from the new document's own load handler; writing the document after the
     fact means the load event has already gone, so the print is scheduled from this side. */
  tab.setTimeout(() => tab.print(), 250);
  return true;
}

/**
 * `buildOgImage` — the 1200×630 link card, composed exactly as the reference composes it.
 *
 * The artwork sits on a white 520px panel at the left, the text block runs down the right, and the
 * status line is coloured by the derived view state. Reproduced rather than replaced because this
 * is the picture LinkedIn shows for the link, and the share modal promises the visitor a preview
 * of that picture.
 */
export async function buildOgImage(c: DemoCredential, svg: string): Promise<string> {
  const W = 1200;
  const H = 630;
  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = H;
  const g = canvas.getContext('2d');
  if (!g) throw new Error('Canvas bağlamı açılamadı');
  if (document.fonts) await document.fonts.ready;

  g.fillStyle = '#F5F2EF';
  g.fillRect(0, 0, W, H);
  g.fillStyle = '#FFFFFF';
  g.fillRect(0, 0, 520, H);

  const cw = canvasW(c.release.canvas);
  const ch = canvasH(c.release.canvas);
  const vis = await loadImage(await credentialPngDataUrl(svg, 520 / Math.max(cw, ch)));
  const sc = Math.min(440 / vis.width, 520 / vis.height);
  const dw = vis.width * sc;
  const dh = vis.height * sc;
  g.drawImage(vis, (520 - dw) / 2, (H - dh) / 2, dw, dh);

  g.fillStyle = '#CF051E';
  g.fillRect(580, 60, 26, 26);
  g.fillStyle = '#F8BA3F';
  g.fillRect(600, 80, 10, 10);
  g.fillStyle = '#000';
  g.font = '600 24px Poppins, sans-serif';
  g.textBaseline = 'alphabetic';
  g.fillText('Happy Place to Work', 622, 82);
  g.fillStyle = '#7B756F';
  g.font = '700 14px Poppins, sans-serif';
  g.fillText(trUpper(credTerms(c, 'tr').verified), 580, 150);

  const wrapText = (
    text: string,
    x: number,
    y: number,
    maxW: number,
    lh: number,
    font: string,
    color: string,
  ): number => {
    g.font = font;
    g.fillStyle = color;
    let line = '';
    let yy = y;
    for (const word of text.split(' ')) {
      const t = line ? `${line} ${word}` : word;
      if (g.measureText(t).width > maxW && line) {
        g.fillText(line, x, yy);
        line = word;
        yy += lh;
      } else {
        line = t;
      }
    }
    if (line) g.fillText(line, x, yy);
    return yy + lh;
  };

  let y = wrapText(credTitle(c), 580, 200, 560, 46, '500 38px Poppins, sans-serif', '#000');
  y = wrapText(displayName(c), 580, y + 4, 560, 34, '600 28px Poppins, sans-serif', '#000');

  const st = credentialViewState(c);
  const statusText: Record<string, string> = {
    active: `Doğrulanmış · ${fmtDate(c.issueDate)}`,
    expired: `Süresi doldu · ${fmtDate(c.expiresAt)}`,
    revoked: 'İptal edildi',
    superseded: 'İptal edildi · yerine yeni kayıt',
    pending_acceptance: 'Onay bekliyor',
    pending_admin_publish: 'Yayın onayı bekliyor',
    draft: 'Taslak',
  };
  g.fillStyle = st === 'active' ? '#1E8A4C' : st === 'expired' ? '#B4720A' : '#000';
  g.font = '600 20px Poppins, sans-serif';
  g.fillText(statusText[st] ?? st, 580, y + 6);

  g.fillStyle = '#8B837C';
  const urlText = publicUrl(c).replace(/^https?:\/\//, '');
  let size = 18;
  g.font = `400 ${size}px Poppins, sans-serif`;
  while (g.measureText(urlText).width > 560 && size > 11) {
    size -= 1;
    g.font = `400 ${size}px Poppins, sans-serif`;
  }
  g.fillText(urlText, 580, H - 50);
  g.fillStyle = '#CF051E';
  g.fillRect(580, H - 30, 560, 4);
  g.fillStyle = '#F8BA3F';
  g.fillRect(1130, H - 36, 14, 14);
  return canvas.toDataURL('image/png');
}
